import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  exportSurveyPayoutCsv,
  getSurveyPayoutSummary,
  saveSurveyPayoutRate,
} from '../supabaseService';
import { encodeUtf8Csv } from '../utils/downloadFile';
import { downloadBytes } from '../utils/downloadFile';

const inputClass = 'rounded-lg border border-de-gold/25 bg-[#0c1a30] px-3 py-2 text-sm text-white';
const dateClass = `${inputClass} min-w-[140px]`;

function monthStartDateInput() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function todayDateInput() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatInr(n) {
  const val = Number(n) || 0;
  return `₹${val.toLocaleString('en-IN')}`;
}

export default function SurveyPayoutTab() {
  const { t } = useLanguage();
  const [fromDate, setFromDate] = useState(monthStartDateInput);
  const [toDate, setToDate] = useState(todayDateInput);
  const [rateInput, setRateInput] = useState('30');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionError, setActionError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [savingRate, setSavingRate] = useState(false);
  const [skippedExport, setSkippedExport] = useState([]);

  const fromIso = useMemo(() => {
    const d = new Date(`${fromDate}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }, [fromDate]);

  const toIso = useMemo(() => {
    const d = new Date(`${toDate}T23:59:59`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }, [toDate]);

  const load = useCallback(async () => {
    if (!fromIso || !toIso || fromDate > toDate) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    setActionError('');
    const { summary: data, error: err } = await getSurveyPayoutSummary(fromIso, toIso);
    if (err) {
      setError(err);
      setSummary(null);
    } else {
      setSummary(data);
      if (data?.ratePerForm != null) setRateInput(String(data.ratePerForm));
    }
    setLoading(false);
  }, [fromIso, toIso, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveRate = async () => {
    setSavingRate(true);
    setActionError('');
    setActionMsg('');
    const { ratePerForm, error: err } = await saveSurveyPayoutRate(rateInput);
    setSavingRate(false);
    if (err) {
      setActionError(err);
      return;
    }
    setRateInput(String(ratePerForm));
    setActionMsg(t('surveyPayoutRateSaved').replace('{rate}', String(ratePerForm)));
    await load();
  };

  const handleExport = async () => {
    const exportTotal = summary?.exportablePayable ?? 0;
    const confirmMsg = t('surveyPayoutExportConfirm')
      .replace('{total}', formatInr(exportTotal));
    if (!window.confirm(confirmMsg)) return;
    setExporting(true);
    setActionError('');
    setActionMsg('');
    setSkippedExport([]);
    const result = await exportSurveyPayoutCsv(fromIso, toIso);
    setExporting(false);
    if (result.error) {
      setActionError(result.error);
      return;
    }
    const bytes = encodeUtf8Csv(result.csv);
    await downloadBytes(bytes, result.filename || 'DE-survey-payout.csv', 'text/csv;charset=utf-8');
    const skipped = result.skippedFreelancers || [];
    setSkippedExport(skipped);
    setActionMsg(
      t('surveyPayoutExportDone')
        .replace('{n}', String(result.lockedCount || 0))
        .replace('{total}', formatInr(result.totalPayable || 0)),
    );
    await load();
  };

  const rows = summary?.rows || [];
  const missing = summary?.missingPaymentDetails || [];
  const dateInvalid = fromDate && toDate && fromDate > toDate;
  const exportablePayable = summary?.exportablePayable ?? 0;
  const canExport = !dateInvalid && exportablePayable > 0;

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-de-gold/20 bg-[#0c1a30]/80 p-4 text-sm text-de-muted">
        <p className="m-0 font-semibold text-de-gold">{t('surveyPayoutIntroTitle')}</p>
        <p className="mt-2 m-0">{t('surveyPayoutIntroHint')}</p>
      </section>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-de-muted">
          {t('surveyPayoutFrom')}
          <input type="date" className={dateClass} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-de-muted">
          {t('surveyPayoutTo')}
          <input type="date" className={dateClass} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-de-muted">
          {t('surveyPayoutRateLabel')}
          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              step="1"
              className={`${inputClass} w-24`}
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
            />
            <Button
              type="button"
              size="sm"
              className="bg-de-gold text-[#14213d]"
              disabled={savingRate}
              onClick={handleSaveRate}
            >
              {savingRate ? '…' : t('surveyPayoutSaveRate')}
            </Button>
          </div>
        </label>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" className="border-de-gold/40 text-de-gold" onClick={load}>
            {t('surveyRetry')}
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-de-gold text-[#14213d]"
            disabled={exporting || loading || !canExport}
            onClick={handleExport}
          >
            {exporting ? t('surveyPayoutExporting') : t('surveyPayoutExportCsv')}
          </Button>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-950/30 p-4 text-sm text-amber-100">
          <p className="m-0 font-semibold">{t('surveyPayoutMissingPaymentTitle')}</p>
          <ul className="mt-2 mb-0 list-inside list-disc">
            {missing.map((m) => (
              <li key={m.code}>{m.name} ({m.code}) — {m.approvedUnlocked ?? m.approved} pending payout</li>
            ))}
          </ul>
          <p className="mt-2 mb-0 text-xs text-amber-200/80">{t('surveyPayoutMissingHintNonBlocking')}</p>
        </div>
      )}

      {skippedExport.length > 0 && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-950/30 p-4 text-sm text-amber-100">
          <p className="m-0 font-semibold">{t('surveyPayoutSkippedTitle')}</p>
          <ul className="mt-2 mb-0 list-inside list-disc">
            {skippedExport.map((s) => (
              <li key={s.code || s.freelancerId}>
                {t('surveyPayoutSkippedLine')
                  .replace('{code}', s.code || '—')
                  .replace('{n}', String(s.approvedUnlocked ?? 0))
                  .replace('{total}', formatInr(s.payable ?? 0))}
              </li>
            ))}
          </ul>
        </div>
      )}

      {dateInvalid && (
        <p className="m-0 text-sm text-de-accent">{t('surveyPayoutDateInvalid')}</p>
      )}

      {error && (
        <p className="m-0 text-sm text-de-accent">{error}</p>
      )}
      {actionError && (
        <p className="m-0 text-sm text-de-accent">{actionError}</p>
      )}
      {actionMsg && (
        <p className="m-0 text-sm text-de-gold">{actionMsg}</p>
      )}

      {loading ? (
        <p className="py-12 text-center text-de-muted">{t('surveyLoading')}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-xl border border-de-gold/20 bg-[#0c1a30]/60 p-4">
              <p className="m-0 text-2xl font-bold text-white">{summary?.totalSubmitted ?? 0}</p>
              <p className="mt-1 text-xs uppercase text-de-muted">{t('surveyPayoutTotalSubmitted')}</p>
            </div>
            <div className="rounded-xl border border-emerald-400/20 bg-emerald-950/20 p-4">
              <p className="m-0 text-2xl font-bold text-emerald-300">{summary?.totalApproved ?? 0}</p>
              <p className="mt-1 text-xs uppercase text-de-muted">{t('surveyPayoutTotalApproved')}</p>
            </div>
            <div className="rounded-xl border border-de-gold/30 bg-de-gold/10 p-4">
              <p className="m-0 text-2xl font-bold text-de-gold">{formatInr(summary?.totalPayable ?? 0)}</p>
              <p className="mt-1 text-xs uppercase text-de-muted">{t('surveyPayoutTotalPayable')}</p>
            </div>
            <div className="rounded-xl border border-de-gold/20 bg-[#0c1a30]/60 p-4">
              <p className="m-0 text-2xl font-bold text-white">{formatInr(summary?.ratePerForm ?? 30)}</p>
              <p className="mt-1 text-xs uppercase text-de-muted">{t('surveyPayoutRatePerForm')}</p>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-de-gold/15 bg-[#0c1a30]/60">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('surveyQrFreelancerName')}</TableHead>
                  <TableHead>{t('surveyQrUniqueId')}</TableHead>
                  <TableHead>{t('surveyPayoutColSubmitted')}</TableHead>
                  <TableHead>{t('surveyPayoutColApproved')}</TableHead>
                  <TableHead>{t('surveyPayoutColPendingExport')}</TableHead>
                  <TableHead>{t('surveyPayoutColLocked')}</TableHead>
                  <TableHead>{t('surveyPayoutColRejected')}</TableHead>
                  <TableHead>{t('surveyPayoutColPending')}</TableHead>
                  <TableHead>{t('surveyPayoutColPayable')}</TableHead>
                  <TableHead>{t('surveyPayoutColUpi')}</TableHead>
                  <TableHead>{t('surveyPayoutColBank')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="py-8 text-center text-de-muted">
                      {t('surveyPayoutEmpty')}
                    </TableCell>
                  </TableRow>
                ) : rows.map((row) => (
                  <TableRow key={row.freelancerId || row.code}>
                    <TableCell className="font-medium text-white">{row.name}</TableCell>
                    <TableCell><Badge className="font-mono bg-de-gold/20 text-de-gold">{row.code}</Badge></TableCell>
                    <TableCell>{row.submitted}</TableCell>
                    <TableCell className="text-emerald-300">{row.approved}</TableCell>
                    <TableCell className="font-semibold text-de-gold">{row.approvedUnlocked ?? 0}</TableCell>
                    <TableCell className="text-slate-400">{row.approvedLocked ?? 0}</TableCell>
                    <TableCell className="text-red-300">{row.rejected}</TableCell>
                    <TableCell className="text-amber-200">{row.pending}</TableCell>
                    <TableCell className="font-semibold text-de-gold">{formatInr(row.payableInr)}</TableCell>
                    <TableCell className="text-xs">
                      {row.upiId || (
                        <span className="text-de-accent">
                          {(row.approvedUnlocked ?? 0) > 0 ? t('surveyPayoutMissing') : '—'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-de-muted">
                      {row.bankAccount && row.bankIfsc
                        ? `${row.bankAccount} / ${row.bankIfsc}`
                        : ((row.approvedUnlocked ?? 0) > 0 && !row.upiId ? t('surveyPayoutMissing') : '—')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
